import { Button, List, Modal, Tag, Typography } from 'antd';
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { IClassroomUpdateStatus } from '../../../../interfaces/classrooms';
import { formatDate } from '../../../../services/date';

interface UpdateProgressModalProps {
  open: boolean;
  onClose: () => void;
  updateStatus: IClassroomUpdateStatus;
}

type StatusKey = 'updated' | 'processing' | 'queued' | 'failed';

const statusMap: Record<
  StatusKey,
  { label: string; color: string; icon: JSX.Element }
> = {
  updated: {
    label: 'Atualizado',
    color: 'green',
    icon: <CheckCircleOutlined />
  },
  processing: {
    label: 'Em processamento',
    color: 'blue',
    icon: <LoadingOutlined />
  },
  queued: {
    label: 'Na fila',
    color: 'default',
    icon: <ClockCircleOutlined />
  },
  failed: {
    label: 'Não atualizado',
    color: 'red',
    icon: <ClockCircleOutlined />
  }
};

export const UpdateProgressModal = ({
  open,
  onClose,
  updateStatus
}: UpdateProgressModalProps) => {
  // Use updateStatus from props for rendering
  const mapped = updateStatus;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title="Atualizar conteúdo da disciplina nas turmas"
      width={800}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          marginBottom: 16
        }}
      >
        {mapped.done ? (
          <CheckCircleFilled style={{ fontSize: 20, color: '#52C41A' }} />
        ) : (
          <ClockCircleOutlined style={{ fontSize: 20, color: '#1890ff' }} />
        )}
        <div>
          <Typography.Text strong>
            {mapped.done ? 'Atualização concluída' : 'Atualização em andamento'}
          </Typography.Text>
          <Typography.Paragraph style={{ margin: 0 }}>
            {mapped.done
              ? 'A atualização do conteúdo da disciplina nas turmas foi concluída.'
              : 'Esse processo pode demorar um tempo. Você pode continuar esse processo em segundo plano'}
          </Typography.Paragraph>
        </div>
      </div>

      <List
        bordered
        dataSource={[
          ...(mapped.success_classrooms || []),
          ...(mapped.pending_classrooms || []),
          ...(mapped.failed_classrooms || [])
        ]}
        renderItem={item => {
          let statusKey = 'queued';

          if (mapped.success_classrooms.some(c => c.id === item.id)) {
            statusKey = 'updated';
          }

          if (mapped.failed_classrooms.some(c => c.id === item.id)) {
            statusKey = 'failed';
          }
          // Add logic for processing if needed
          const allowedStatusKeys: StatusKey[] = [
            'updated',
            'processing',
            'queued',
            'failed'
          ];
          const safeStatusKey: StatusKey = allowedStatusKeys.includes(
            statusKey as StatusKey
          )
            ? (statusKey as StatusKey)
            : 'queued';
          return (
            <List.Item key={item.id}>
              <div
                style={{ display: 'flex', alignItems: 'center', width: '100%' }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{}}>
                    <div style={{ marginRight: 8 }}>
                      <Typography.Text strong>
                        {item.code} - {item.description}
                      </Typography.Text>
                    </div>
                    <div style={{ fontSize: 12 }}>
                      {`${formatDate(
                        item.starts_at,
                        'DD/MM/YYYY'
                      )} > ${formatDate(item.ends_at, 'DD/MM/YYYY')}`}
                    </div>
                  </div>
                </div>
                <Tag
                  icon={statusMap[safeStatusKey].icon}
                  color={
                    statusMap[safeStatusKey].color === 'default'
                      ? undefined
                      : statusMap[safeStatusKey].color
                  }
                >
                  {statusMap[safeStatusKey].label}
                </Tag>
              </div>
            </List.Item>
          );
        }}
        style={{ marginBottom: 24 }}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" onClick={onClose}>
          {updateStatus.done ? 'Concluir' : 'Continuar em segundo plano'}
        </Button>
      </div>
    </Modal>
  );
};
